/**
 * Format a date string in a given IANA timezone.
 * Falls back gracefully if timezone is invalid.
 */
export function formatInTz(iso, timezone, opts = {}) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat(undefined, {
      timeZone: timezone,
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      ...opts,
    }).format(d);
  } catch {
    return new Date(iso).toLocaleString();
  }
}

// "February 25, 2026 at 3:00 PM" — matches Bloq's standard human-friendly format
export function formatLongDateTimeInTz(iso, timezone) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const date = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone, month: "long", day: "2-digit", year: "numeric",
    }).format(d);
    const time = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone, hour: "numeric", minute: "2-digit", hour12: true,
    }).format(d);
    return `${date} at ${time}`;
  } catch {
    return new Date(iso).toLocaleString();
  }
}

// "February 25, 2026"
export function formatLongDateInTz(iso, timezone) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("en-US", {
      timeZone: timezone, month: "long", day: "2-digit", year: "numeric",
    }).format(d);
  } catch {
    return new Date(iso).toDateString();
  }
}

// "3:00 PM"
export function formatTime12hInTz(iso, timezone) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("en-US", {
      timeZone: timezone, hour: "numeric", minute: "2-digit", hour12: true,
    }).format(d);
  } catch {
    return new Date(iso).toLocaleTimeString();
  }
}

// Short TZ label ("EST", "PST", "GMT+5:30")
export function tzLabel(iso, timezone) {
  if (!iso) return timezone;
  try {
    const d = new Date(iso);
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone, timeZoneName: "short",
    }).formatToParts(d);
    const tn = parts.find((p) => p.type === "timeZoneName");
    return tn?.value || timezone;
  } catch {
    return timezone;
  }
}

export function formatTimeInTz(iso, timezone) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat(undefined, {
      timeZone: timezone,
      hour: "numeric",
      minute: "2-digit",
    }).format(d);
  } catch {
    return new Date(iso).toLocaleTimeString();
  }
}

export function formatDateInTz(iso, timezone) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat(undefined, {
      timeZone: timezone,
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(d);
  } catch {
    return new Date(iso).toDateString();
  }
}

export function relativeFromNow(iso) {
  if (!iso) return "";
  const d = new Date(iso).getTime();
  const now = Date.now();
  const diff = d - now;
  const abs = Math.abs(diff);
  const min = 60 * 1000;
  const hr = 60 * min;
  const day = 24 * hr;
  if (abs < min) return diff > 0 ? "in a moment" : "just now";
  if (abs < hr) return diff > 0 ? `in ${Math.round(abs / min)}m` : `${Math.round(abs / min)}m ago`;
  if (abs < day) return diff > 0 ? `in ${Math.round(abs / hr)}h` : `${Math.round(abs / hr)}h ago`;
  return diff > 0
    ? `in ${Math.round(abs / day)}d`
    : `${Math.round(abs / day)}d ago`;
}

// Common timezones for picker. Order roughly by UTC offset, west to east.
// Includes labels so users see both city and current UTC offset.
export const COMMON_TIMEZONES = [
  "Pacific/Midway",
  "Pacific/Honolulu",
  "America/Anchorage",
  "America/Los_Angeles",
  "America/Vancouver",
  "America/Phoenix",
  "America/Denver",
  "America/Edmonton",
  "America/Chicago",
  "America/Mexico_City",
  "America/Winnipeg",
  "America/New_York",
  "America/Toronto",
  "America/Bogota",
  "America/Lima",
  "America/Caracas",
  "America/Halifax",
  "America/Santiago",
  "America/St_Johns",
  "America/Sao_Paulo",
  "America/Argentina/Buenos_Aires",
  "Atlantic/South_Georgia",
  "Atlantic/Azores",
  "UTC",
  "Europe/London",
  "Europe/Lisbon",
  "Europe/Dublin",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Madrid",
  "Europe/Amsterdam",
  "Europe/Brussels",
  "Europe/Stockholm",
  "Europe/Rome",
  "Europe/Vienna",
  "Europe/Zurich",
  "Europe/Warsaw",
  "Africa/Johannesburg",
  "Europe/Athens",
  "Europe/Istanbul",
  "Europe/Helsinki",
  "Europe/Moscow",
  "Africa/Cairo",
  "Asia/Jerusalem",
  "Asia/Riyadh",
  "Asia/Dubai",
  "Asia/Tehran",
  "Asia/Karachi",
  "Asia/Kabul",
  "Asia/Kolkata",
  "Asia/Kathmandu",
  "Asia/Dhaka",
  "Asia/Yangon",
  "Asia/Bangkok",
  "Asia/Jakarta",
  "Asia/Singapore",
  "Asia/Hong_Kong",
  "Asia/Shanghai",
  "Asia/Taipei",
  "Asia/Manila",
  "Asia/Kuala_Lumpur",
  "Asia/Tokyo",
  "Asia/Seoul",
  "Australia/Perth",
  "Australia/Adelaide",
  "Australia/Darwin",
  "Australia/Brisbane",
  "Australia/Sydney",
  "Australia/Melbourne",
  "Pacific/Guam",
  "Pacific/Auckland",
  "Pacific/Fiji",
];

// Returns a friendly label like "America/New_York (GMT-5)" for the picker dropdown.
export function tzLabelWithOffset(tz) {
  try {
    const now = new Date();
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz, timeZoneName: "shortOffset",
    }).formatToParts(now);
    const off = parts.find((p) => p.type === "timeZoneName")?.value || "";
    return off ? `${tz.replaceAll("_", " ")} · ${off}` : tz.replaceAll("_", " ");
  } catch {
    return tz;
  }
}

export function userBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}
